import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import Spinner from './Spinner';
import { setLiveMessage } from './ScreenReaderLive';

const ReviewModal = ({ isOpen, onClose, requestId, revieweeName, token, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a star rating');
      return;
    }

    if (comment.length > 140) {
      toast.error('Comment must be 140 characters or less');
      return;
    }

    try {
      setSubmitting(true);
      setLiveMessage('Submitting review...');

      const response = await fetch(
        `${(process.env.REACT_APP_BACKEND_URL || '')}/api/requests/${requestId}/review`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            rating,
            comment: comment.trim() || null
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit review');
      }

      setLiveMessage('Review submitted successfully');
      toast.success('Review submitted successfully');
      onSuccess();
      onClose();
      
      // Reset form
      setRating(0);
      setComment('');
    } catch (error) {
      setLiveMessage('Failed to submit review');
      toast.error(error.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Leave a Review for {revieweeName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Star Rating */}
          <div>
            <Label>Rating *</Label>
            <div className="flex gap-2 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="focus:outline-none focus:ring-2 focus:ring-brand-gold rounded"
                  aria-label={`Rate ${star} stars`}
                >
                  <Star
                    size={32}
                    className={`transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'fill-brand-gold text-brand-gold'
                        : 'text-gray-600'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-brand-muted mt-1">
                {rating} star{rating !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <Label>Comment (Optional)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience working together..."
              rows={3}
              maxLength={140}
              className="resize-none"
            />
            <p className="text-xs text-brand-muted mt-1 text-right">
              {comment.length}/140 characters
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-black"
              disabled={submitting || rating === 0}
              aria-busy={submitting}
            >
              {submitting && <Spinner className="mr-2" />}
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
